import React, { useState, useEffect } from 'react';
import { API_URL } from '../../utils/constants';

function TemplateManagement({ token }) {
  const [templates, setTemplates] = useState([]);
  const [formData, setFormData] = useState({ type: 'sms', title: '', content: '' });
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (token) {
      fetchTemplates();
    }
  }, [token]);

  const fetchTemplates = async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      } else {
        alert('템플릿 로드 실패');
      }
    } catch (err) {
      console.error('템플릿 로드 실패:', err);
      alert('템플릿 로드 중 오류 발생');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.type || !formData.title || !formData.content) {
      alert('유형, 제목, 내용을 모두 입력해주세요.');
      return;
    }

    const url = editingTemplate 
      ? `${API_URL}/templates/${editingTemplate.id}` 
      : `${API_URL}/templates`;
    const method = editingTemplate ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        alert(editingTemplate ? '템플릿이 수정되었습니다.' : '템플릿이 등록되었습니다.');
        setFormData({ type: 'sms', title: '', content: '' });
        setEditingTemplate(null);
        fetchTemplates();
      } else {
        const error = await response.json();
        alert(error.error || '템플릿 저장 실패');
      }
    } catch (err) {
      console.error('템플릿 저장 실패:', err);
      alert('템플릿 저장 중 오류 발생');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('이 템플릿을 삭제하시겠습니까?')) return;
    
    try {
      const response = await fetch(`${API_URL}/templates/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        alert('템플릿이 삭제되었습니다.');
        fetchTemplates();
      } else {
        alert('템플릿 삭제 실패');
      }
    } catch (err) {
      console.error('템플릿 삭제 실패:', err);
      alert('템플릿 삭제 중 오류 발생');
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({ type: template.type, title: template.title, content: template.content });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancel = () => {
    setEditingTemplate(null);
    setFormData({ type: 'sms', title: '', content: '' });
  };

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '400px',
        fontSize: '16px',
        color: '#7f8c8d'
      }}>
        템플릿을 불러오는 중...
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '20px'
    }}>
      {/* 헤더 */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '30px',
        marginBottom: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{
          margin: '0 0 10px 0',
          fontSize: '28px',
          fontWeight: 'bold',
          color: '#2c3e50'
        }}>
          문자 템플릿 관리
        </h2>
        <p style={{
          margin: 0,
          fontSize: '14px',
          color: '#7f8c8d'
        }}>
          자주 사용하는 문자 메시지 템플릿을 등록하고 관리하세요.
        </p>
      </div>

      {/* 폼 */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '30px',
        marginBottom: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: editingTemplate ? '2px solid #f39c12' : 'none'
      }}>
        <h3 style={{
          margin: '0 0 20px 0',
          fontSize: '20px',
          fontWeight: 'bold',
          color: '#2c3e50'
        }}>
          {editingTemplate ? '📝 템플릿 수정' : '➕ 새 템플릿 등록'}
        </h3>
        
        <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '15px' }}>
          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '600',
              color: '#2c3e50'
            }}>
              유형 *
            </label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '14px',
                border: '2px solid #ecf0f1',
                borderRadius: '8px',
                boxSizing: 'border-box',
                outline: 'none',
                backgroundColor: 'white'
              }}
              required
            >
              <option value="sms">SMS</option>
              <option value="alimtalk">알림톡</option>
              <option value="email">이메일</option>
            </select>
          </div>

          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '600',
              color: '#2c3e50'
            }}>
              제목 *
            </label>
            <input
              type="text"
              placeholder="예: 작업 완료 안내"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '14px',
                border: '2px solid #ecf0f1',
                borderRadius: '8px',
                boxSizing: 'border-box',
                outline: 'none'
              }}
              required
            />
          </div>
          
          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '600',
              color: '#2c3e50'
            }}>
              내용 *
            </label>
            <textarea
              placeholder="예: 안녕하세요, [업체명]입니다. 요청하신 작업이 완료되었습니다."
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              style={{
                width: '100%',
                minHeight: '150px',
                padding: '12px',
                fontSize: '14px',
                border: '2px solid #ecf0f1',
                borderRadius: '8px',
                boxSizing: 'border-box',
                outline: 'none',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
              required
            />
            <div style={{
              marginTop: '5px',
              fontSize: '12px',
              color: '#7f8c8d'
            }}>
              💡 Tip: [업체명], [담당자] 등 자리 표시자를 사용할 수 있습니다.
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
            <button
              type="submit"
              style={{
                flex: 1,
                padding: '14px',
                backgroundColor: editingTemplate ? '#f39c12' : '#27ae60',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 'bold',
                transition: 'all 0.2s'
              }}
            >
              {editingTemplate ? '수정 완료' : '등록하기'}
            </button>
            {editingTemplate && (
              <button
                type="button"
                onClick={handleCancel}
                style={{
                  flex: 1,
                  padding: '14px',
                  backgroundColor: '#95a5a6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  transition: 'all 0.2s'
                }}
              >
                취소
              </button>
            )}
          </div>
        </form>
      </div>

      {/* 템플릿 목록 */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '30px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h3 style={{
            margin: 0,
            fontSize: '20px',
            fontWeight: 'bold',
            color: '#2c3e50'
          }}>
            템플릿 목록
          </h3>
          <span style={{
            padding: '6px 12px',
            backgroundColor: '#ecf0f1',
            borderRadius: '20px',
            fontSize: '14px',
            fontWeight: 'bold',
            color: '#2c3e50'
          }}>
            {templates.length}개
          </span>
        </div>

        {templates.length === 0 ? (
          <div style={{
            padding: '60px 20px',
            textAlign: 'center',
            color: '#95a5a6'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '10px' }}>📝</div>
            <div style={{ fontSize: '16px' }}>등록된 템플릿이 없습니다.</div>
            <div style={{ fontSize: '14px', marginTop: '5px' }}>위 폼에서 새 템플릿을 등록해보세요.</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '15px' }}>
            {templates.map((template) => (
              <div
                key={template.id}
                style={{
                  padding: '20px',
                  border: '2px solid #ecf0f1',
                  borderRadius: '8px',
                  transition: 'all 0.2s',
                  backgroundColor: 'white'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3498db';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(52,152,219,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#ecf0f1';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: '20px'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: '18px',
                      fontWeight: 'bold',
                      color: '#2c3e50',
                      marginBottom: '10px'
                    }}>
                      {template.title}
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: '#7f8c8d',
                      lineHeight: '1.6',
                      whiteSpace: 'pre-wrap',
                      backgroundColor: '#f8f9fa',
                      padding: '12px',
                      borderRadius: '6px',
                      border: '1px solid #e9ecef'
                    }}>
                      {template.content}
                    </div>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    flexShrink: 0
                  }}>
                    <button
                      onClick={() => handleEdit(template)}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#3498db',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '600',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#2980b9'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#3498db'}
                    >
                      수정
                    </button>
                    <button
                      onClick={() => handleDelete(template.id)}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#e74c3c',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '600',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#c0392b'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#e74c3c'}
                    >
                      삭제
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default TemplateManagement;
